import { RecoveryState } from "@ondit/ui";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { GATE_EVENT, type GateReason, loginUrl, type Me, type Org } from "~/core/api";
import { useAccountLocale, useI18n, useT } from "~/core/i18n";
import { apiError } from "~/core/query";
import { PageLoading } from "~/shell/page-loading";
import { useViewer } from "./viewer";

export { useViewer, viewerQuery } from "./viewer";

/** Whether this person may change the account itself: its money, its plan, its
 *  brands, who is in it. The routes ask again. */
export function isAdmin(me: Me, org: Org): boolean {
	return me.user.is_admin || org.role === "owner" || org.role === "admin";
}

/**
 * Who is signed in, or the way to sign in.
 *
 * A 401 here is not an error to report: it means there is no session at all,
 * which is a sign-in. Anything else is worth showing, because the difference
 * between "we could not reach the server" and "you are signed out" is the
 * difference between waiting and doing something.
 */
export function SessionGate({ children }: { children: (me: Me) => ReactNode }) {
	const t = useT();
	const { locale } = useI18n();
	const me = useViewer();
	const error = apiError(me.error);
	// The account's stored language, for somebody who has not chosen one here.
	useAccountLocale(me.data?.user.locale);

	if (me.isFetching && !me.data && !error) return <PageLoading />;

	if (error?.isUnauthorized) {
		window.location.href = loginUrl(window.location.pathname);
		return null;
	}

	if (!me.data)
		return (
			<div className="content">
				<RecoveryState
					title={t("state.loadError")}
					description={
						!error || error.messageKey === "errors.internal" ? t("state.loadErrorBody") : error.localized(locale)
					}
					hint={t("state.sessionErrorHint")}
					retryLabel={t("state.retry")}
					retryingLabel={t("state.retrying")}
					retrying={me.isFetching}
					onRetry={() => void me.refetch()}
				/>
			</div>
		);

	return <>{children(me.data)}</>;
}

/**
 * The application's answer to a refusal, whichever screen it came from.
 *
 * Out of credits opens the packs; a lapsed plan opens the plan. The screen that
 * was refused still shows its own sentence — this is the thing to do about it,
 * over the top of it, so "top up and try again" is two clicks and not a search
 * for the billing page.
 */
export function GateListener({ onCredits, onSubscription }: { onCredits: () => void; onSubscription: () => void }) {
	useEffect(() => {
		const onGate = (event: Event) => {
			const reason = (event as CustomEvent<GateReason>).detail;
			if (reason === "credits") onCredits();
			else onSubscription();
		};
		window.addEventListener(GATE_EVENT, onGate);
		return () => window.removeEventListener(GATE_EVENT, onGate);
	}, [onCredits, onSubscription]);
	return null;
}
